import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { List, Input, Button, Form, Popconfirm, message, Card, Select, Tag } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import Loading from "./Loading";

const AddForm = styled(Form)`
  display: flex;
  gap: 0.5em;
  margin-bottom: 1.5em;
`;

const EditRow = styled.div`
  display: flex;
  gap: 0.5em;
  flex: 1;
`;

export default function NamedItemManager({
  title,
  placeholder,
  dataKey,
  getQuery,
  addMutation,
  updateMutation,
  deleteMutation,
  extraField,
}) {
  // extraField 為選用的第二個欄位（目前用於分類的收入/支出型別）。
  // 未提供時整個元件行為與原本只管 name 時完全一致（幣別設定不受影響）。
  const extraDefault = extraField ? extraField.defaultValue : undefined;
  const findOption = (value) =>
    extraField && extraField.options.find((option) => option.value === value);

  // 401 由 apolloClient 全域統一處理（提示 + 導向登入）。
  const { loading, data, refetch } = useQuery(getQuery, {
    fetchPolicy: "cache-and-network",
  });

  const [newName, setNewName] = useState("");
  const [newExtra, setNewExtra] = useState(extraDefault);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [editingExtra, setEditingExtra] = useState(extraDefault);

  const onError = (error) => message.error(error.message);

  const [addItem, { loading: adding }] = useMutation(addMutation, {
    onCompleted() {
      setNewName("");
      setNewExtra(extraDefault);
      refetch();
    },
    onError,
  });

  const [updateItem, { loading: updating }] = useMutation(updateMutation, {
    onCompleted() {
      setEditingId(null);
      setEditingName("");
      refetch();
    },
    onError,
  });

  const [deleteItem] = useMutation(deleteMutation, {
    onCompleted() {
      refetch();
    },
    onError,
  });

  const handleAdd = () => {
    const name = newName.trim();
    if (!name) return;
    addItem({ variables: { name, ...(extraField && { [extraField.key]: newExtra }) } });
  };

  const startEditing = (item) => {
    setEditingId(item.id);
    setEditingName(item.name);
    if (extraField) {
      setEditingExtra(item[extraField.key] || extraDefault);
    }
  };

  const handleUpdate = () => {
    const name = editingName.trim();
    if (!name) return;
    updateItem({
      variables: { id: editingId, name, ...(extraField && { [extraField.key]: editingExtra }) },
    });
  };

  const items = (data && data[dataKey]) || [];

  return (
    <Card title={title}>
      {loading && !data ? (
        <Loading />
      ) : (
        <>
          <AddForm onFinish={handleAdd}>
            <Input
              placeholder={placeholder}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={20}
            />
            {extraField && (
              <Select value={newExtra} onChange={setNewExtra} style={{ minWidth: 96 }}>
                {extraField.options.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            )}
            <Button
              type="primary"
              htmlType="submit"
              icon={<PlusOutlined />}
              loading={adding}
              disabled={!newName.trim()}
            >
              新增
            </Button>
          </AddForm>

          <List
            locale={{ emptyText: "尚未建立任何項目" }}
            dataSource={items}
            renderItem={(item) =>
              editingId === item.id ? (
                <List.Item>
                  <EditRow>
                    <Input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onPressEnter={handleUpdate}
                      maxLength={20}
                      autoFocus
                    />
                    {extraField && (
                      <Select
                        value={editingExtra}
                        onChange={setEditingExtra}
                        style={{ minWidth: 96 }}
                      >
                        {extraField.options.map((option) => (
                          <Select.Option key={option.value} value={option.value}>
                            {option.label}
                          </Select.Option>
                        ))}
                      </Select>
                    )}
                    <Button
                      type="primary"
                      onClick={handleUpdate}
                      loading={updating}
                      disabled={!editingName.trim()}
                    >
                      儲存
                    </Button>
                    <Button onClick={() => setEditingId(null)}>取消</Button>
                  </EditRow>
                </List.Item>
              ) : (
                <List.Item
                  actions={[
                    <Button type="text" icon={<EditOutlined />} onClick={() => startEditing(item)}>
                      編輯
                    </Button>,
                    <Popconfirm
                      title="確定要刪除這個項目嗎？"
                      okText="刪除"
                      cancelText="取消"
                      onConfirm={() => deleteItem({ variables: { id: item.id } })}
                    >
                      <Button type="text" danger icon={<DeleteOutlined />}>
                        刪除
                      </Button>
                    </Popconfirm>,
                  ]}
                >
                  {extraField && findOption(item[extraField.key]) && (
                    <Tag color={findOption(item[extraField.key]).color}>
                      {findOption(item[extraField.key]).label}
                    </Tag>
                  )}
                  {item.name}
                </List.Item>
              )
            }
          />
        </>
      )}
    </Card>
  );
}

NamedItemManager.defaultProps = {
  extraField: null,
};

NamedItemManager.propTypes = {
  title: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  dataKey: PropTypes.string.isRequired,
  getQuery: PropTypes.objectOf(PropTypes.any).isRequired,
  addMutation: PropTypes.objectOf(PropTypes.any).isRequired,
  updateMutation: PropTypes.objectOf(PropTypes.any).isRequired,
  deleteMutation: PropTypes.objectOf(PropTypes.any).isRequired,
  extraField: PropTypes.shape({
    key: PropTypes.string.isRequired,
    defaultValue: PropTypes.string,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        color: PropTypes.string,
      })
    ).isRequired,
  }),
};
